import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import CalculatieOverviewTable from "../../components/calculations/CalculatieOverviewTable";
import CalculatieWorkspace from "../../components/calculations/CalculatieWorkspace";
import { EMPTY_FORM, EMPTY_LABOR, EMPTY_MATERIAL, mapCalculatieToForm, serializeForm } from "../../components/calculations/utils";
import { useUser } from "../../services/context/UserContext";
import { openOCI } from "../../utils/openOCI";
import { hasFinancialPricingAccess } from "../../utils/pricingUi";

export default function CalculatiesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const [clients, setClients] = useState([]);
  const [opnames, setOpnames] = useState([]);
  const [projects, setProjects] = useState([]);
  const [calculaties, setCalculaties] = useState([]);
  const [hourTypes, setHourTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [copying, setCopying] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingId, setEditingId] = useState("");
  const [selectedClientFilter, setSelectedClientFilter] = useState("all");
  const [form, setForm] = useState(EMPTY_FORM);
  const [tuPunchoutLoading, setTuPunchoutLoading] = useState(false);
  const [duplicatePreview, setDuplicatePreview] = useState(null);
  const [duplicatePreviewLoading, setDuplicatePreviewLoading] = useState(false);
  const [refreshPreview, setRefreshPreview] = useState(null);
  const [refreshPreviewLoading, setRefreshPreviewLoading] = useState(false);

  const showFinancials = useMemo(
    () => hasFinancialPricingAccess(user?.permissions),
    [user?.permissions]
  );

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError("");
      const [clientRes, opnameRes, projectRes, calculatieRes, hourTypeRes] = await Promise.all([
        api.get("/clients"),
        api.get("/opnames"),
        api.get("/projects"),
        api.get("/calculaties"),
        api.get("/tenant-hour-types"),
      ]);
      setClients(Array.isArray(clientRes.data) ? clientRes.data : []);
      setOpnames(Array.isArray(opnameRes.data) ? opnameRes.data : []);
      setProjects(Array.isArray(projectRes.data) ? projectRes.data : []);
      setCalculaties(Array.isArray(calculatieRes.data) ? calculatieRes.data : []);
      setHourTypes(Array.isArray(hourTypeRes.data) ? hourTypeRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Calculaties laden mislukt");
    } finally {
      setLoading(false);
    }
  }

  const filteredCalculaties = useMemo(() => {
    if (selectedClientFilter === "all") return calculaties;
    return calculaties.filter(
      (item) => String(item?.clientId?._id || item?.clientId || "") === String(selectedClientFilter)
    );
  }, [calculaties, selectedClientFilter]);

  const selectedCalculatie = useMemo(
    () => calculaties.find((item) => String(item._id) === String(editingId)) || null,
    [calculaties, editingId]
  );

  const selectedProject = useMemo(() => {
    const projectId = String(form.projectId || "").trim();
    return projects.find((item) => String(item._id) === projectId) || null;
  }, [form.projectId, projects]);

  const selectedClient = useMemo(() => {
    const clientId = String(form.clientId || "").trim();
    return clients.find((item) => String(item._id) === clientId) || null;
  }, [clients, form.clientId]);

  const selectedOpname = useMemo(() => {
    const opnameId = String(form.opnameId || "").trim();
    return opnames.find((item) => String(item._id) === opnameId) || null;
  }, [form.opnameId, opnames]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.clientId || !String(form.title || "").trim()) {
      setError("Klant/opdrachtgever en calculatienaam zijn verplicht.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      const payload = serializeForm(form);
      if (editingId) {
        await api.put(`/calculaties/${editingId}`, payload);
      } else {
        await api.post("/calculaties", payload);
      }
      await loadData();
      if (!editingId) {
        resetForm();
      } else {
        setSuccess("Calculatie bijgewerkt.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Calculatie opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function applyToProject() {
    if (!editingId) return;
    try {
      setApplying(true);
      setError("");
      setSuccess("");
      await api.post(`/calculaties/${editingId}/apply-to-project`);
      await loadData();
      setSuccess("Calculatie doorgezet naar projectbudget.");
    } catch (err) {
      setError(err?.response?.data?.message || "Doorzetten naar projectbudget mislukt");
    } finally {
      setApplying(false);
    }
  }

  async function copyCalculatie() {
    if (!editingId) return;
    try {
      setCopying(true);
      setError("");
      setSuccess("");
      const res = await api.post(`/calculaties/${editingId}/revise`);
      await loadData();
      setSelectedClientFilter("all");
      startEdit(res?.data || {});
      setSuccess("Nieuwe revisie aangemaakt vanuit deze calculatie.");
    } catch (err) {
      setError(err?.response?.data?.message || "Calculatie revisie maken mislukt");
    } finally {
      setCopying(false);
    }
  }

  async function duplicateSameClientCalculatie() {
    if (!editingId) return;
    try {
      setDuplicating(true);
      setError("");
      setSuccess("");
      const res = await api.post(`/calculaties/${editingId}/duplicate-same-client`);
      await loadData();
      if (form.clientId) {
        setSelectedClientFilter(String(form.clientId));
      }
      startEdit(res?.data || {});
      setSuccess("Nieuwe zelfstandige calculatie aangemaakt voor dezelfde klant.");
    } catch (err) {
      setError(err?.response?.data?.message || "Calculatie dupliceren voor dezelfde klant mislukt");
    } finally {
      setDuplicating(false);
    }
  }

  async function duplicateCalculatie({ clientId, contactPersonId, title }) {
    if (!editingId) return;
    try {
      setDuplicating(true);
      setError("");
      setSuccess("");
      const payload = {
        clientId,
        contactPersonId: contactPersonId || undefined,
        title: title || undefined,
      };
      const res = await api.post(`/calculaties/${editingId}/copy-to-client`, payload);
      await loadData();
      setSelectedClientFilter(String(clientId || "all"));
      startEdit(res?.data || {});
      setSuccess("Kopie aangemaakt voor andere klant.");
    } catch (err) {
      setError(err?.response?.data?.message || "Calculatie kopiëren naar andere klant mislukt");
      throw err;
    } finally {
      setDuplicating(false);
    }
  }

  async function previewDuplicateCalculatie({ clientId, contactPersonId, title }) {
    if (!editingId) return;
    try {
      setDuplicatePreviewLoading(true);
      setDuplicatePreview(null);
      setError("");
      setSuccess("");
      const payload = {
        clientId,
        contactPersonId: contactPersonId || undefined,
        title: title || undefined,
      };
      const res = await api.post(`/calculaties/${editingId}/copy-to-client-preview`, payload);
      setDuplicatePreview(res?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Preview voor kopie naar andere klant mislukt");
      throw err;
    } finally {
      setDuplicatePreviewLoading(false);
    }
  }

  async function previewRefreshPricing({ pricingDate }) {
    if (!editingId) return;
    try {
      setRefreshPreviewLoading(true);
      setRefreshPreview(null);
      setError("");
      setSuccess("");
      const res = await api.post(`/calculaties/${editingId}/refresh-pricing-preview`, {
        pricingDate: pricingDate || undefined,
      });
      setRefreshPreview(res?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Prijspreview actualiseren mislukt");
      throw err;
    } finally {
      setRefreshPreviewLoading(false);
    }
  }

  async function applyRefreshedPricing({ pricingDate }) {
    if (!editingId) return;
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      await api.post(`/calculaties/${editingId}/refresh-pricing`, {
        pricingDate: pricingDate || undefined,
      });
      await loadData();
      setRefreshPreview(null);
      setSuccess("Prijzen geactualiseerd.");
    } catch (err) {
      setError(err?.response?.data?.message || "Prijzen actualiseren mislukt");
    } finally {
      setSaving(false);
    }
  }

  function resetForm() {
    setEditingId("");
    setForm(EMPTY_FORM);
    setSuccess("");
    setError("");
    setDuplicatePreview(null);
    setRefreshPreview(null);
    syncEditParam("");
  }

  function startEdit(item) {
    const nextId = String(item._id || "");
    setEditingId(nextId);
    setForm(mapCalculatieToForm(item));
    setError("");
    setSuccess("");
    setDuplicatePreview(null);
    setRefreshPreview(null);
    syncEditParam(nextId);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function startTuPunchout() {
    if (!editingId) return;

    try {
      setTuPunchoutLoading(true);
      setError("");
      setSuccess("");
      const params = new URLSearchParams();
      params.set("edit", editingId);

      const payload = {
        returnPath: `/pl/calculaties?${params.toString()}`,
      };

      const res = await api.post(`/calculaties/${editingId}/integrations/tu/punchout`, payload);
      const url = res?.data?.tu?.url;
      if (!openOCI(url)) {
        throw new Error("OCI catalogus-url ontbreekt");
      }
      setSuccess("Catalogus openen...");
    } catch (err) {
      setError(err?.response?.data?.message || "OCI catalogus openen mislukt");
    } finally {
      setTuPunchoutLoading(false);
    }
  }

  function openCalculatiePdf() {
    if (!editingId) return;
    const apiBase = String(api.defaults.baseURL || "").trim();
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Geen geldige sessie gevonden voor PDF-export.");
      return;
    }

    fetch(`${apiBase}/calculaties/${editingId}/pdf`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data?.message || "Calculatie PDF openen mislukt");
        }
        return response.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
      })
      .catch((err) => {
        setError(err.message || "Calculatie PDF openen mislukt");
      });
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const editId = String(params.get("edit") || "").trim();
    const importStatus = String(params.get("ociImport") || params.get("tuImport") || "")
      .trim()
      .toLowerCase();
    const importedCount = Number(params.get("ociImportedCount") || params.get("tuImportedCount") || 0);
    const importError = String(params.get("ociError") || params.get("tuError") || "").trim();

    if (editId && calculaties.length > 0 && editId !== editingId) {
      const selected = calculaties.find((item) => String(item._id) === editId);
      if (selected) {
        setEditingId(editId);
        setForm(mapCalculatieToForm(selected));
      }
    }

    if (!importStatus) return;

    if (importStatus === "success") {
      setError("");
      setSuccess(
        importedCount > 0
          ? `${importedCount} producten succesvol geimporteerd in calculatie.`
          : "Producten succesvol geimporteerd in calculatie."
      );
      loadData();
    } else {
      setSuccess("");
      setError(importError || "Bestelling niet afgerond, probeer opnieuw");
    }

    params.delete("ociImport");
    params.delete("ociImportedCount");
    params.delete("ociError");
    params.delete("tuImport");
    params.delete("tuImportedCount");
    params.delete("tuError");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true }
    );
  }, [calculaties, editingId, location.pathname, location.search, navigate]);

  function updateLine(section, index, key, value) {
    setForm((prev) => ({
      ...prev,
      [section]: prev[section].map((line, lineIndex) =>
        lineIndex === index ? { ...line, [key]: value } : line
      ),
    }));
  }

  function updateField(key, value) {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "clientId") {
        next.contactPersonId = "";
        if (!value) {
          next.projectId = "";
          next.opnameId = "";
        }
      }
      return next;
    });
  }

  function handleLaborHourTypeChange(index, hourTypeId) {
    const selected = hourTypes.find((item) => String(item._id) === String(hourTypeId)) || null;
    setForm((prev) => ({
      ...prev,
      laborLines: prev.laborLines.map((line, lineIndex) =>
        lineIndex === index
          ? {
              ...line,
              hourTypeId,
              hourTypeCode: selected?.code || "",
              hourTypeName: selected?.name || "",
              sellRate: selected ? toNumberValue(selected.sellRate) : line.sellRate,
              costRate: selected ? toNumberValue(selected.costRate) : line.costRate,
              description:
                selected && !String(line.description || "").trim()
                  ? selected.name || line.description
                  : line.description,
            }
          : line
      ),
    }));
  }

  function addLine(section) {
    setForm((prev) => ({
      ...prev,
      [section]: [...prev[section], section === "laborLines" ? { ...EMPTY_LABOR } : { ...EMPTY_MATERIAL }],
    }));
  }

  function removeLine(section, index) {
    setForm((prev) => ({
      ...prev,
      [section]: prev[section].length > 1 ? prev[section].filter((_, i) => i !== index) : prev[section],
    }));
  }

  function syncEditParam(nextId) {
    const params = new URLSearchParams(location.search || "");
    if (nextId) {
      params.set("edit", nextId);
    } else {
      params.delete("edit");
    }
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true }
    );
  }

  return (
    <div className="space-y-6">
      <CalculatieWorkspace
        form={form}
        editingId={editingId}
        clients={clients}
        opnames={opnames}
        saving={saving}
        applying={applying}
        copying={copying}
        duplicating={duplicating}
        tuPunchoutLoading={tuPunchoutLoading}
        selectedClient={selectedClient}
        selectedOpname={selectedOpname}
        selectedProject={selectedProject}
        selectedCalculatie={selectedCalculatie}
        hourTypes={hourTypes}
        error={error}
        success={success}
        showFinancials={showFinancials}
        duplicatePreview={duplicatePreview}
        duplicatePreviewLoading={duplicatePreviewLoading}
        refreshPreview={refreshPreview}
        refreshPreviewLoading={refreshPreviewLoading}
        onSubmit={handleSubmit}
        onReset={resetForm}
        onFieldChange={updateField}
        onLineChange={updateLine}
        onHourTypeChange={handleLaborHourTypeChange}
        onAddLine={addLine}
        onRemoveLine={removeLine}
        onApplyToProject={applyToProject}
        onCopy={copyCalculatie}
        onDuplicateSameClient={duplicateSameClientCalculatie}
        onDuplicate={duplicateCalculatie}
        onPreviewDuplicate={previewDuplicateCalculatie}
        onPreviewRefreshPricing={previewRefreshPricing}
        onApplyRefreshedPricing={applyRefreshedPricing}
        onOpenPdf={openCalculatiePdf}
        onOpenCatalog={startTuPunchout}
        projects={projects}
      />

      <CalculatieOverviewTable
        loading={loading}
        calculaties={filteredCalculaties}
        clients={clients}
        selectedClientFilter={selectedClientFilter}
        onClientFilterChange={setSelectedClientFilter}
        onSelect={startEdit}
        activeId={editingId}
        showFinancials={showFinancials}
      />
    </div>
  );
}

function toNumberValue(value) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
}
